// This module uses d3 to render the job-graph

import AppModel from '../DataModel/AppModel';
import moment from 'moment-timezone';
import d3 from 'd3';
import {uiMsg} from '../utils/Languages';

/**
 * Helper function that takes a color and darkens it by a percentage.
 * See this stackoverflow question for more information: https://stackoverflow.com/questions/5560248/programmatically-lighten-or-darken-a-hex-color-or-rgb-and-blend-colors
 *
 * @param color
 * @param percent
 * @returns {string}
 */
function shadeColor(color, percent) {
  var f=parseInt(color.slice(1),16),t=percent<0?0:255,p=percent<0?percent*-1:percent,R=f>>16,G=f>>8&0x00FF,B=f&0x0000FF;
  return "#"+(0x1000000+(Math.round((t-R)*p)+R)*0x10000+(Math.round((t-G)*p)+G)*0x100+(Math.round((t-B)*p)+B)).toString(16).slice(1);
}

function drawReportGraph(graph_div) {
  graph_div.innerHTML="";
  var job = AppModel.currentJob;
  if(!job) {
    console.error("No job to plot graph for");
    return null;
  }
  var locale = AppModel.locale;
  var time_zone = AppModel.time_zone;




  var	margin = {top: 60, right: 220, bottom: 60, left: 40},
  width = AppModel.uiSession.deviceWidth - margin.left - margin.right,
  height = AppModel.uiSession.deviceHeight - margin.top - margin.bottom;

  var	x = d3.time.scale().range([0, width]);
  var	y = d3.scale.linear().range([height, 0]);

  // Scale the range of the data
  // We use the start_time and end_time of the job as boundaries
  var start_time = job.start_time;
  var end_time = job.end_time;
  // If we don't have an end-time yet, we make the graph
  if(!end_time) {
    var lastMeasurement = job.lastMeasureReadTime;
    if(lastMeasurement) {
      // Extend the graph to 10 minutes into the future :
      end_time = lastMeasurement;
      // If it is less than 5 minutes since the job started :
      // This is just to
      if( (+end_time - +start_time) < 1000*60*5 ) {
        end_time = new Date(+start_time + 1000*60*5);
      }
    }
    else {
      // If we don't have a last read.. what do we have ??
      // Just set the end date to an hour after start
      end_time = new Date(+start_time + 1000*60*60);
    }
  }

  x.domain([start_time,end_time]);

  var y_max_val = calculateYMaxValueFromJob(job);
  
  y.domain([0, y_max_val]);

  var numberOfXTicks=10;
  var numberOfYTicks=10;

  var	xAxis = d3.svg.axis()
  .scale(x)
  .orient("bottom")
  .ticks(numberOfXTicks)
  .tickFormat(function(d) {
    return moment(d).tz(time_zone).locale(locale).format("HH:mm")
    //return d.toLocaleTimeString(locale,{timeZone:time_zone}).substring(0,5);
  });

  var	yAxis = d3.svg.axis()
  .scale(y)
  .orient("left")
  .ticks(numberOfYTicks);

  var	line = d3.svg.line()
  .x(function(d) { return x(new Date(d[0])); })
  .y(function(d) { return y(d[1]); });

  var	svg = d3.select(graph_div)
  .append("svg")
  .attr("width", width + margin.left + margin.right)
  .attr("height", height + margin.top + margin.bottom)
  .append("g")
  .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

  svg.append("g")			// Add the X Axis
  .attr("class", "x axis")
  .attr("transform", "translate(0," + height + ")")
  .call(xAxis)
  .selectAll("text")
  .style("text-anchor", "end")
  .attr("dx", "-.8em")
  .attr("dy", ".15em")
  .attr("transform", "rotate(-45)" );

  svg.append("g")			// Add the X Axis grid
  .attr("class", "grid")
  .call(xAxis
    .tickSize(height)
    .tickFormat("")
  );

  svg.append("g")			// Add the Y Axis
  .attr("class", "y temperature axis")
  .call(yAxis);

  svg.append("g")			// Add the Y Axis grid
  .attr("class", "grid")
  .call(yAxis
    .tickSize(-width)
    .tickFormat("")
  );




  // Adding labels to our diagram, we need to make sure they do not
  // overlap, so we keep a list of them to iterate over later:
  var label_nodes = [];

  // Iterating over the sensors and drawing the respective graph for each one
  job.measurements.forEach(function(measurement, sensorIndex){
    let sensorName = job.machine_sensors_map.get(sensorIndex) && job.machine_sensors_map.get(sensorIndex).name || sensorIndex;
    drawGraphsAndLabelsForMeasurement(measurement, sensorName, sensorIndex);
  });

  function drawGraphsAndLabelsForMeasurement(measurement, sensorName, sensorIndex){

    // Map of graphs that we should try to draw . The key is the key that is used for storing the data on a job measurement map
    let graphs = {
      
      'RelativeHumidity' : {
        uiMsg: 'REPORT_GRAPH_HUMIDTY',
        color: '#ff484d'
      },
      'H2O2' : {
        uiMsg: 'REPORT_GRAPH_H2O2',
        color: '#5cdb65'
      }
    };

    for (let key in graphs) {
      if (graphs.hasOwnProperty(key)) {

        let data = measurement.get(key);
        let graph = graphs[key];
        let graphColor = shadeColor(graph.color, (-0.15)*sensorIndex); // Darken colors for each sensor index

        if(data && data.length > 1) {
          svg.append("path")
            .attr("stroke", graphColor)
            .attr("stroke-width", '1.5')
            .attr("d", line(data));

          let last = data.length-1;
          label_nodes.push(
            svg.append("text")
              .attr("transform", "translate(" + (width) + "," + y(data[last][1]) + ")")
              .attr("dy", ".35em")
              .attr("text-anchor", "start")
              .style("fill", graphColor)
              .text(uiMsg(graph.uiMsg) + ' ( '+ sensorName +' ) ')
              .node()
            // We are only adding a single node here, but it returns an array
          );
        }

      }
    }

  }

  /**
   * We use the same Y-axis for temperature (C), humidity (%) and H2O2
   * concentration (PPM)
   *
   * @param job
   * @returns {number}
   */
  function calculateYMaxValueFromJob(job){
    let yMax = 0;
    job.measurements.forEach(function(measurement){
      let humidity_data = measurement.get("RelativeHumidity") || [];
      let H2O2_data = measurement.get("H2O2") || [];      
      if(humidity_data.length > 0) {
        yMax = Math.max(yMax,
          d3.max(humidity_data, function(d) { return d[1] })
        );
      }      
      if(H2O2_data.length > 0) {
        yMax = Math.max(yMax,
          d3.max(H2O2_data, function(d) { return d[1] })
        );
      }      
    });
    return yMax;
  }

  function parseTranslateString(trans) {
    // the string looks like this : translate(x,y)
    if(!trans) {
      console.error("parseTranslateString called with empty trans string");
      return {"x":0,"y":0}
    }
    var values = trans.split("(")[1].split(")")[0].split(/[ ,]+/)
    var pos = {x:+values[0],y:+values[1]}
    return pos;
  }
  // Check if labels overlap and if they do, keep moving them around until
  // they don't :
  var alpha = 1;
  var spacing = 14;
  function relax() {
    var textLabels = d3.selectAll(label_nodes);
    var again = false;
    textLabels.each(function (d, i) {
      var a = this; // current element
      var da = d3.select(a);
      var pos1 = parseTranslateString(da.attr("transform"));            
      var y1=pos1.y;
      textLabels.each(function (d, j) {
        var b = this;
        // a & b are the same element and don't collide.
        if (a == b) return;
        var db = d3.select(b);
        // a & b are on opposite sides of the chart and
        // don't collide
        if (da.attr("text-anchor") != db.attr("text-anchor")) return;
        // Now let's calculate the distance between
        // these elements.
        var pos2 = parseTranslateString(db.attr("transform"));
        var y2=pos2.y;
        // If the element hasn't been rendered yet
        var deltaY = y1 - y2;
        // If spacing is greater than our specified spacing,
        // they don't collide.
        if (Math.abs(deltaY) > spacing) return;
        // If the labels collide, we'll push each
        // of the two labels up and down a little bit.
        var again = true;
        var sign = deltaY > 0 ? 1 : -1;
        var adjust = sign * alpha;
        // Setting the transform:
        var da_trans="translate("+pos1.x+","+(y1 + adjust)+")";
        da.attr("transform",da_trans);
        var db_trans="translate("+pos2.x+","+(y2 - adjust)+")";
        db.attr("transform",db_trans);
      });
    });
    // Repeat until no labals overlap anymore :
    if(again ) relax();
  }
  relax();
}

export {
  drawReportGraph
};



/** WEBPACK FOOTER **
 ** ./src/components/ReportGraph.js
 **/